// ============================================================
// CaseSettlementSection.tsx — the case's SETTLEMENT section (Stage 4 round
// 2a, Case file group). The CFA, every settlement input, the base costs and
// a LIVE deductions preview. The saved statement (B4) sits below.
//
// Access (server-enforced, controllers/settlementController settlementGate):
// case access AND "See colleagues' billing" (or admin) — the settlement is
// built from everyone's recorded costs. Without it GET answers
// { locked: true } and this shows a locked card, never the figures.
//
// SAVE CONVENTIONS (batch 2 — READ-FIRST; nothing money-related autosaves)
//   • Every card shows a clean READ view of what's saved. Its pencil opens a
//     DRAFT of that card (one card at a time); nothing is sent until Save, and
//     Save sends just that card's CHANGED fields in ONE PATCH carrying the
//     baseUpdatedAt the draft was opened from (the Claim card's pattern,
//     sections/ClaimCard). Cancel throws the draft away.
//       – 409 → the "someone else changed this" banner (Reload);
//       – 409 code STATEMENT_LOCKED → its message, and the page re-reads;
//       – 400 + field → the error under that field; Save stays available.
//     While any field is invalid (a PenceField holding bad text — useBadFields,
//     audit M11 — a half-typed date, a future date that's blocked, a recovery
//     over its amount, an override with no reason) Save is disabled with
//     "Fix the highlighted fields to save.".
//     Costs also carries the base-costs override (PUT, reason required,
//     audited); ATE & disbursements also carries the recoveries (one PATCH per
//     changed row) — both sent by that card's one Save.
//   • The CFA — its own editor (CfaPanel's modal): explicit Save.
//   • Interim payments — the list is the read view; Add / Edit / Remove per
//     row (audited).
//   • UnsavedGuard while a card is dirty. After each save: emitCaseChange
//     ('settlementInputs' / 'cfa') — the statement panel below re-reads.
// LOCK: while a statement is PREPARED / APPROVED (state.statementLock) every
// figure card is read-only (no pencil) with ONE box above the figures and
// "Send back for changes" (the statement panel's #statement-send-back).
// FRESH: the section re-reads whenever the case changes elsewhere on screen
// (useCaseChange: funding, CFA, litigation friend, client details, statement,
// court order, inputs), keeping an open card's draft. (Audit M15: the section
// itself always mounts fresh — ViewingPane swaps the component and flushes
// the case autosave first; the "still no CFA" after setting funding was that
// funding never creates the CFA record — the page now offers "Create from
// firm defaults" wherever it says there's none.)
// DATES: accident ≤ CFA signed ≤ issued ≤ settlement; the rest in between.
// Out-of-order dates get an amber line under the field (cfaDates.ts — the
// preview lists the same, by code); "received" / "issued" / "agreed" in the
// future are blocked (the server 400s; the page says so at once).
// The preview runs on the saved inputs PLUS the open card's draft (and an
// unsaved CFA / interim form) — "Includes unsaved changes" while it does.
//
// LAYOUT (Settlement redesign, Option 2 — "Money + next step"): StatementPanel
// lays out the sentence, the next step on the left, and the ONE money summary +
// History on the right; when the SECTION itself is narrower than
// SETTLEMENT_TWO_COL_MIN px (measured — the case menu may be open or folded, the
// app sidebar wide or narrow) it's one column. The figure cards live in a DRAWER
// (FiguresDrawer) opened from the money summary, with the net updating live at
// its foot beside the field being changed. The cards stay mounted while it's
// shut, so an open card's draft can't be lost. The case menu folds by the same
// rule as on every case section (caseNavFold).
//
// LIVE PERMISSIONS: a change that NARROWS access drops every figure at once
// and re-reads; any other change re-reads the server copy but keeps unsaved
// work (an open card's draft, a dirty CFA draft + stage text).
//
// Place: the section is a case place (?caseId=…&section=settlement) — Back and
// the URL work like every other section. No layout jump: the placeholder is
// built from the SAME grids / field footprints as the loaded cards.
// ============================================================
import { useElementWidth } from '../case/caseNavFold'
import { textDateToIso, ukDate } from '../../utils/civilDate'
import { useLivePermissions } from '../../hooks/useLivePermissions'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  getSettlementApi, previewSettlementApi, patchSettlementMatterApi, confirmClientAdultApi, saveSettlementCardApi,
  createInterimApi, updateInterimApi, deleteInterimApi, createCfaApi, getCaseStatementsApi, sendBackStatementApi,
} from '../../api/casevoApi'
import SendBackDialog from './statement/SendBackDialog'
import { useLoadPhase } from '../../hooks/useLoadPhase'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import { SectionHead } from '../case/sections/sectionKit'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import DateInput from '../shared/DateInput'
import ActionBox, { B_SM_PRIMARY } from '../shared/ActionBox'
import { FOCUSABLE, isTabStop } from '../shared/ModalFrame'
import {
  BIcon, CARD, B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, B_GHOST_SM, LABEL, NOTE, INPUT, TH, TD, Skel, FIELD_ERROR,
  errorText, fmtDate, fmtDuration, money, standardVatPence,
} from '../billing/billingUi'
import { categoryLabel } from '../../types/billing.types'
import { Banner, EditFooter, LockedFooter, EditPencil, FieldRow, FieldSkel, FigureCard, PenceField, ReadRow, useBadFields } from './settlementUi'
import { SettingsSwitch } from '../settings/settingsUi'
import CfaPanel, { CFA_CARD_ID, CFA_REVIEW_BUTTON_ID, cfaEditableOf, cfaChanges } from './CfaPanel'
import SettlementPreview, { PreviewOptionsFold, SettlementBottomBar, type MoneyEditTarget, type Payee } from './SettlementPreview'
import DeductionsBreakdown from './statement/DeductionsBreakdown'
import StatementPanel from './StatementPanel'
import type { Setup } from './statement/nextStep'
import { REPRESENTATIVE_TYPE_LABELS, type CaseStatements, type Representatives } from '../../types/settlementStatement.types'

import type { FixTarget } from '../../types/settlementStatement.types'
import { navigateTo } from '../../navigation/navigate'
import { NO_ACCIDENT_DATE_NOTE, futureDateError, settlementDateWarnings, type DateWarning } from './cfaDates'
import { stageDraftOf, type StageDraft } from '../admin/PiSettlementDefaultsPanel'
import { CFA_STAGES, CFA_STAGE_LABEL } from '../../types/settlement.types'
import { useCasevoStore } from '../../store/useCasevoStore'
import { emitCaseChange, useCaseChange, type CaseChangeKind } from '../../utils/caseChangeEvents'
import { useLiveEditNotice } from '../../hooks/useLiveEditNotice'
import LiveUpdateNote from '../shared/LiveUpdateNote'
import type {
  CfaEditable, CfaStage, CfaTerms, InterimPayment, MatterValues, PreviewEdits, PreviewOptions, PreviewResult,
  ProceedingsLevel, ProceedingsStage, RecoveryStatus, SettlementDisbursement, SettlementState,
} from '../../types/settlement.types'

type Loaded = Extract<SettlementState, { locked: false }>

const STAGE_LABEL: Record<ProceedingsStage, string> = { PRE_ISSUE: 'Not issued', ISSUED_COUNTY_COURT: 'Issued — County Court', ISSUED_HIGH_COURT: 'Issued — High Court' }
const LEVEL_LABEL: Record<ProceedingsLevel, string> = { FIRST_INSTANCE: 'First instance (25% cap)', APPEAL: 'Appeal (100% cap)' }
const RECOVERY_LABEL: Record<RecoveryStatus, string> = { PENDING: 'Not yet known', RECOVERED: 'Recovered', PART_RECOVERED: 'Part recovered', IRRECOVERABLE: 'Not recoverable' }
const pastLossTotal = (m: MatterValues): number => m.pastLossEarningsPence + m.pastLossCarePence + m.pastLossMobilityPence + m.pastLossOtherPence
const cfaTermsOnly = (d: CfaEditable): Partial<CfaTerms> => {
  const { outsetDeductionEstimateBasisPoints: _o, wordingVersion: _w, ...t } = d
  return t
}
// A stored (free-text) date as dd/mm/yyyy for messages.
const dmy = (raw: string): string => { const iso = textDateToIso(raw); return iso ? ukDate(iso) : raw }
const statusOf = (e: unknown): number | undefined => (e as { response?: { status?: number } }).response?.status
const codeOf = (e: unknown): string | undefined => (e as { response?: { data?: { code?: string } } }).response?.data?.code
const fieldOf = (e: unknown): string | undefined => (e as { response?: { data?: { field?: string } } }).response?.data?.field
// A disbursement recovery the save would refuse (more than the amount / VAT).
const recoveryBad = (d: SettlementDisbursement, x: { recoveredPence: number; recoveredVatPence: number }): boolean =>
  x.recoveredPence > d.amountPence || x.recoveredVatPence > d.vatPence
const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b)
const MUTED = 'text-casevo-text-soft'

// ── The figure cards and the matter fields each one owns ──
export type CardKey = 'damages' | 'pastLoss' | 'cru' | 'costs' | 'ate' | 'court' | 'client'
export const CARD_FIELDS: Record<CardKey, (keyof MatterValues)[]> = {
  damages: ['pslaPence', 'futureLossPence', 'interestPence', 'quantumDate'],
  pastLoss: ['pastLossEarningsPence', 'pastLossCarePence', 'pastLossMobilityPence', 'pastLossOtherPence'],
  cru: ['cruPastLossEarningsPence', 'cruPastCarePence', 'cruPastMobilityPence', 'cruCertificateDate'],
  costs: ['recoveredCostsPence', 'recoveredCostsVatPence', 'recoveredCostsAgreedOn', 'counselSuccessFeePence'],
  ate: ['ateProvider', 'atePremiumPence', 'ateRecoveredPence', 'ateCommissionPence', 'ateCommissionDisclosed'],
  court: ['proceedingsStage', 'proceedingsLevel', 'stageReached', 'issuedOn', 'court', 'adverseCostsOffsetPence'],
  client: ['protectedParty'],
}
const CARD_TITLE: Record<CardKey, string> = {
  damages: 'Damages', pastLoss: 'Past loss', cru: 'CRU', costs: 'Costs', ate: 'ATE & disbursements', court: 'Court & proceedings', client: 'Client',
}
const cardDomId = (k: CardKey): string => `settlement-card-${k}`
const ONE_CARD_ID = 'settlement-one-card-at-a-time'
type OverrideDraft = { on: boolean; net: number | null; vat: number | null; reason: string }
type RecoveryPatch = { recoveredPence?: number; recoveredVatPence?: number; recoveryStatus?: RecoveryStatus }
type DisbDraft = Record<string, RecoveryPatch>
interface Editing {
  card: CardKey
  // The saved values + version the draft was opened from (Save sends only
  // what differs from these, against this base → a stale base 409s).
  opened: MatterValues
  base: string | null
  draft: MatterValues
  override: OverrideDraft
  recoveries: DisbDraft
}
const overrideOf = (s: Loaded): OverrideDraft => {
  const o = s.matter.baseCostsOverride
  return o ? { on: true, net: o.netPence, vat: o.vatPence, reason: o.reason } : { on: false, net: s.baseCosts.netPence, vat: s.baseCosts.vatPence, reason: '' }
}
const overrideChanged = (s: Loaded, o: OverrideDraft): boolean => {
  const saved = s.matter.baseCostsOverride
  if (!o.on) return !!saved
  return !saved || saved.netPence !== o.net || saved.vatPence !== o.vat || saved.reason !== o.reason.trim()
}
// The changed fields of the open card (only those).
const cardBody = (e: Editing): Partial<MatterValues> => {
  const body: Record<string, unknown> = {}
  for (const k of CARD_FIELDS[e.card]) if (!same(e.draft[k], e.opened[k])) body[k] = e.draft[k]
  return body as Partial<MatterValues>
}

// ── Placeholder — the loaded layout's own structure (option A): the same
// grid, the CFA summary card, stacked figure cards made of 38px label/box
// rows, and the preview column — so nothing jumps when the figures land. ──
const SkelRow: React.FC = () => (
  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-[16px] items-center py-[5px]"><Skel className="h-[14px] w-40" /><Skel className="h-[28px] w-[120px]" /></div>
)
const SkelFigure: React.FC<{ rows: number }> = ({ rows }) => (
  <div className={CARD}>
    <div className="flex items-start gap-3 px-5 pt-4 pb-2">
      <Skel className="w-[34px] h-[34px] rounded-[10px]" />
      <div className="flex-1"><Skel className="h-[18px] w-32" /><Skel className="h-[14px] w-56 max-w-full mt-[5px]" /></div>
      <Skel className="h-[16px] w-24 mt-[6px]" />
    </div>
    <div className="px-5 pb-4">{Array.from({ length: rows }, (_, i) => <SkelRow key={i} />)}</div>
  </div>
)
// Two columns (figures | preview) only when the section has room.
export const SETTLEMENT_TWO_COL_MIN = 880
const GRID_2 = 'grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'
const Placeholder: React.FC<{ twoCol: boolean }> = ({ twoCol }) => (
  <div aria-hidden="true" className={`grid grid-cols-1 ${twoCol ? GRID_2 : ''} gap-4 items-start`}>
    <div className="flex flex-col gap-4">
      <div className={CARD}>
        <div className="flex items-start gap-3 px-5 pt-4 pb-3"><Skel className="w-[34px] h-[34px] rounded-[10px]" /><div className="flex-1"><Skel className="h-[18px] w-56" /><Skel className="h-[14px] w-40 mt-[5px]" /></div><Skel className="h-[30px] w-[104px]" /></div>
        <div className="px-5 pb-4"><div className="flex flex-wrap gap-[8px]">{['w-[150px]', 'w-[140px]', 'w-[110px]', 'w-[140px]', 'w-[160px]'].map((w, i) => <Skel key={i} className={`h-[26px] rounded-full ${w}`} />)}</div><Skel className="h-[16px] w-72 max-w-full mt-[10px]" /></div>
      </div>
      {/* Damages · Past loss · CRU · Costs · ATE & disbursements · Interim · Court · Client */}
      <SkelFigure rows={4} /><SkelFigure rows={4} /><SkelFigure rows={4} /><SkelFigure rows={7} /><SkelFigure rows={6} /><SkelFigure rows={2} /><SkelFigure rows={6} /><SkelFigure rows={2} />
    </div>
    <div className={`${CARD} ${twoCol ? 'block' : 'hidden'}`}>
      <div className="flex items-start gap-3 px-5 pt-4 pb-3"><Skel className="w-[34px] h-[34px] rounded-[10px]" /><div className="flex-1"><Skel className="h-[18px] w-44" /><Skel className="h-[14px] w-52 mt-[5px]" /><Skel className="h-[20px] w-48 mt-[6px]" /></div></div>
      <div className="px-5 pb-5">
        <div className="grid grid-cols-2 gap-[12px] pb-[14px]"><FieldSkel /><FieldSkel /><div className="col-span-2"><FieldSkel /></div><Skel className="h-[18px] w-64 col-span-2" /></div>
        <Skel className="h-[96px] w-full mt-[14px] rounded-[12px]" />
        <Skel className="h-[14px] w-full mt-[14px] rounded-[7px]" />
        <div className="grid grid-cols-2 gap-x-[18px] gap-y-[6px] mt-[8px]">{Array.from({ length: 6 }, (_, i) => <Skel key={i} className="h-[14px]" />)}</div>
        {Array.from({ length: 9 }, (_, i) => <Skel key={i} className="h-[40px] w-full mt-[8px]" />)}
      </div>
    </div>
  </div>
)

// (Batch 2) ONE name for ONE place: "Client details → Litigation friend".
// Opens Client details with ?focus=litigation-friend (that section scrolls to
// and focuses the card with id `litigation-friend`).
export const LITIGATION_FRIEND_PLACE = 'Client details → Litigation friend'
export const goToLitigationFriend = (caseId: string): void => {
  void navigateTo({ view: 'caseDetail', caseId, caseSection: 'clientDetails', params: { focus: 'litigation-friend' } })
}
// (B4 follow-ups) "Litigation friend: Jane Hughes (Mother) · Attorney: Tom
// Hughes — Edit in Client details → Litigation friend". None recorded → an
// ActionBox (a child's / protected party's final statement can't issue
// without one) with "Record the litigation friend".
export const LitigationFriendSummary: React.FC<{ caseId: string; r: Representatives | undefined }> = ({ caseId, r }) => {
  const lf = r?.litigationFriendName.trim()
  const rep = r?.representativeType && r.representativeName.trim() ? `${REPRESENTATIVE_TYPE_LABELS[r.representativeType]}: ${r.representativeName.trim()}` : null
  if (!lf) {
    return (
      <ActionBox ariaLabel="No litigation friend recorded" title="No litigation friend recorded"
        message={<>
          The final statement is addressed to them, so it can’t be issued until one is recorded in {LITIGATION_FRIEND_PLACE}.
          {rep ? <span className="block mt-[2px]">{rep}</span> : null}
        </>}
        actions={[{ label: 'Record the litigation friend', kind: 'primary', onClick: () => goToLitigationFriend(caseId) }]} />
    )
  }
  return (
    <div role="status" aria-label="Litigation friend summary" className="flex items-center gap-[10px] flex-wrap rounded-[12px] border border-casevo-border bg-casevo-surface px-[14px] py-[10px] text-[13px] text-casevo-forest">
      <span className="flex-1 min-w-0">
        Litigation friend: <b className="font-semibold">{lf}</b>{r?.litigationFriendRelationship.trim() ? ` (${r.litigationFriendRelationship.trim()})` : ''}
        {rep ? <span className="text-casevo-text-soft"> · {rep}</span> : null}
      </span>
      <button type="button" className={B_SECONDARY_SM} onClick={() => goToLitigationFriend(caseId)}>
        <BIcon name="pen" size={13} />Edit in {LITIGATION_FRIEND_PLACE}
      </button>
    </div>
  )
}

// Scroll an element into view and focus it (or its first field).
const scrollFocus = (el: Element | null, block: ScrollLogicalPosition = 'center'): void => {
  const h = el as HTMLElement | null
  if (!h) return
  h.scrollIntoView?.({ behavior: 'smooth', block })
  h.focus?.({ preventScroll: true })
}

// The drawer the figure cards live in. Shut, it stays mounted but hidden.
const FiguresDrawer: React.FC<{ open: boolean; onClose: () => void; foot: React.ReactNode; children: React.ReactNode }> = ({ open, onClose, foot, children }) => {
  const panel = useRef<HTMLDivElement>(null)
  const closeBtn = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!open) return undefined
    const back = document.activeElement as HTMLElement | null
    closeBtn.current?.focus()
    const onKey = (e: KeyboardEvent): void => {
      // A window opened inside the drawer has the keys first.
      if ((e.key !== 'Escape' && e.key !== 'Tab') || document.querySelector('[role="dialog"][aria-modal="true"]:not([data-figures-panel])')) return
      if (e.key === 'Escape') { onClose(); return }
      // Tab stays inside, as in every other window (the same rule as ModalFrame's).
      const stops = Array.from(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(isTabStop).filter((el) => el.getClientRects().length > 0)
      if (!stops.length) return
      const first = stops[0]; const last = stops[stops.length - 1]
      const at = document.activeElement
      if (e.shiftKey ? (at === first || !panel.current?.contains(at)) : (at === last || !panel.current?.contains(at))) {
        e.preventDefault()
        ;(e.shiftKey ? last : first).focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); back?.focus?.() }
  }, [open, onClose])
  return (
    <div className={open ? 'fixed inset-0 z-[60]' : 'hidden'} data-figures-drawer={open ? 'open' : 'shut'}>
      <div className="absolute inset-0 bg-black/25" aria-hidden="true" onClick={onClose} />
      <div ref={panel} role={open ? 'dialog' : undefined} aria-modal={open ? true : undefined} aria-label={open ? 'The settlement figures' : undefined} data-figures-panel=""
        className="absolute top-0 right-0 bottom-0 w-full sm:w-[600px] max-w-full bg-casevo-offwhite border-l border-casevo-border shadow-2xl flex flex-col">
        <div className="flex items-center gap-[10px] px-5 py-3 border-b border-casevo-border bg-casevo-surface">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-casevo-text-soft">Settlement</div>
            <h3 className="m-0 text-[16px] font-semibold text-casevo-forest">The figures</h3>
          </div>
          <button ref={closeBtn} type="button" className={B_SECONDARY_SM} onClick={onClose}>Close</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pt-4 pb-4 flex flex-col gap-4">{children}</div>
        <div className="sticky bottom-0 px-4 pb-3">{foot}</div>
      </div>
    </div>
  )
}

const RELOAD_KINDS: CaseChangeKind[] = ['funding', 'cfa', 'litigationFriend', 'clientDetails', 'statement', 'courtOrder', 'settlementInputs']

const CaseSettlementSection: React.FC<{ caseId: string }> = ({ caseId }) => {
  const [state, setState] = useState<SettlementState | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const phase = useLoadPhase(state !== null || loadError !== null)
  const currentCaseClient = useCasevoStore((st) => (st.currentCase?.id === caseId ? st.currentCase.client ?? null : null))
  // Bumped on every server read — the preview re-runs on the new saved copy.
  const [rev, setRev] = useState(0)

  // Drafts.
  const [editing, setEditing] = useState<Editing | null>(null)
  const [cfaDraft, setCfaDraft] = useState<CfaEditable | null>(null)
  const [stageText, setStageText] = useState<StageDraft>(stageDraftOf([]))
  const [interimForm, setInterimForm] = useState<{ id: string | null; amount: number | null; receivedOn: string | null; payer: string; note: string } | null>(null)
  // "Try different options": null = the statement's own details.
  const [trying, setTrying] = useState<PreviewOptions | null>(null)
  // The open card: dates still being typed, the server's refusal of a field,
  // the save itself.
  const [typing, setTyping] = useState<Record<string, boolean>>({})
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof MatterValues, string>>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  // A save refused because a statement locked the figures meanwhile.
  const [lockMessage, setLockMessage] = useState<string | null>(null)
  // (Follow-ups) The "Figures locked" box's send-back reason box (open / busy / refused).
  const [sendingBack, setSendingBack] = useState<{ busy: boolean; error: string | null } | null>(null)
  const bad = useBadFields()
  // CFA: "Create from firm defaults" (card, preview box, statement fix) and
  // the statement's "cfa" fix opening the editor.
  const [cfaCreate, setCfaCreate] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null })
  const [cfaOpenReq, setCfaOpenReq] = useState(0)
  // The case's statements — only for the payee-aware labels (destination,
  // court order recorded). The statement panel loads its own copy.
  const [stmt, setStmt] = useState<CaseStatements | null>(null)
  // The section's OWN width decides two columns vs one column + bottom bar.
  const [sectionRef, sectionWidth] = useElementWidth()
  const twoCol = sectionWidth === null || sectionWidth >= SETTLEMENT_TWO_COL_MIN
  // The drawer's "Show breakdown" modal, and the drawer.
  const [breakdownOpen, setBreakdownOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // ── Load ──
  const apply = useCallback((s: SettlementState) => {
    setState(s); setRev((n) => n + 1)
    if (s.locked) return
    setCfaDraft(s.cfa ? cfaEditableOf(s.cfa) : null)
    setStageText(stageDraftOf(s.cfa?.stages ?? []))
  }, [])
  // (Audit fix batch 1, 31 Oct, M6) Every read of the settlement takes a
  // number; only the NEWEST one's answer is used — a slow earlier read (an
  // event's refresh) can never put older figures back over a newer save.
  const stateSeq = useRef(0)
  const stmtSeq = useRef(0)
  const load = useCallback(async () => {
    const n = ++stateSeq.current
    try { const s = await getSettlementApi(caseId); if (n !== stateSeq.current) return; apply(s); setLoadError(null) } catch (e) { if (n === stateSeq.current) setLoadError(errorText(e, 'Couldn’t load the settlement.')) }
  }, [caseId, apply])
  const loadStmt = useCallback(async () => {
    const n = ++stmtSeq.current
    try { const r = await getCaseStatementsApi(caseId); if (n === stmtSeq.current) setStmt(r) } catch { /* the labels fall back to the capacity's default */ }
  }, [caseId])
  useEffect(() => { void load(); void loadStmt() }, [load, loadStmt])
  // The latest drafts, for refresh (it runs from a stale closure).
  const latest = useRef<{ state: SettlementState | null; cfaDraft: CfaEditable | null; stageText: StageDraft }>({ state: null, cfaDraft: null, stageText: stageDraftOf([]) })
  latest.current = { state, cfaDraft, stageText }
  // A non-narrowing re-read: the server copy, keeping unsaved work — the open
  // card's draft lives apart from the saved copy (and keeps the base it was
  // opened from, so its Save still 409s if someone else saved meanwhile).
  const refresh = useCallback(async () => {
    let s: SettlementState
    const n = ++stateSeq.current
    try { s = await getSettlementApi(caseId); if (n !== stateSeq.current) return; setLoadError(null) } catch (e) { if (n === stateSeq.current) setLoadError(errorText(e, 'Couldn’t load the settlement.')); return }
    const prev = latest.current.state
    // Was locked / now locked / first load: nothing unsaved to keep.
    if (s.locked || !prev || prev.locked) { if (s.locked) setEditing(null); apply(s); return }
    setRev((n) => n + 1)
    // CFA: a dirty draft (terms or stage text) stays, against the CFA it was
    // made from — so its Save still 409s if someone else saved meanwhile.
    const { cfaDraft: d, stageText: st } = latest.current
    const cfaDirty = !!prev.cfa && !!d && !!s.cfa && s.cfa.id === prev.cfa.id
      && (Object.keys(cfaChanges(cfaEditableOf(prev.cfa), d)).length > 0 || JSON.stringify(st) !== JSON.stringify(stageDraftOf(prev.cfa.stages)))
    if (cfaDirty) setState({ ...s, cfa: prev.cfa })
    else {
      setState(s)
      setCfaDraft(s.cfa ? cfaEditableOf(s.cfa) : null)
      setStageText(stageDraftOf(s.cfa?.stages ?? []))
    }
  }, [caseId, apply])
  // ★ LIVE permission change (e.g. "See colleagues' billing" removed): drop
  // every figure on screen AT ONCE, then re-read — the server answers with the
  // locked card if access is gone (or the figures, if it was just granted).
  // Any other change re-reads too, but keeps unsaved work (refresh).
  useLivePermissions((narrowed) => {
    if (narrowed) { setState(null); setEditing(null); setPreview(null); void load(); return }
    void refresh()
  })
  // (Batch 2 / audit M15) Something changed elsewhere on this screen (the
  // Funding page, the CFA, Client details, the statement …): re-read. Our own
  // announcements are skipped (we've just re-read).
  const ownEmit = useRef(false)
  const announce = useCallback((kind: CaseChangeKind) => {
    ownEmit.current = true
    try { emitCaseChange(caseId, kind) } finally { ownEmit.current = false }
  }, [caseId])
  useCaseChange(caseId, RELOAD_KINDS, (_kind, info) => {
    if (ownEmit.current) return
    void refresh()
    if (info.kinds.some((k) => k === 'statement' || k === 'courtOrder' || k === 'litigationFriend')) void loadStmt()
  })
  // (D12 live updates) Someone else's change while a card is open → the card's
  // draft stays (refresh keeps it) and the page says "Updated by … — Reload".
  const liveEdit = useLiveEditNotice(caseId, RELOAD_KINDS, editing !== null)
  // After a write here: re-read (an open card's draft is kept).
  const reload = useCallback(async () => {
    const n = ++stateSeq.current
    const s = await getSettlementApi(caseId)
    if (n !== stateSeq.current) return
    setState(s); setRev((x) => x + 1)
    if (s.locked) { setEditing(null); return }
    setCfaDraft(s.cfa ? cfaEditableOf(s.cfa) : null)
    setStageText(stageDraftOf(s.cfa?.stages ?? []))
  }, [caseId])

  // ── CFA: create from the firm defaults ──
  const createCfa = useCallback(async (): Promise<void> => {
    setCfaCreate({ busy: true, error: null })
    try {
      await createCfaApi(caseId)
      await reload()
      announce('cfa')
      setCfaCreate({ busy: false, error: null })
    } catch (e) {
      // Someone made one meanwhile (409): show it.
      if (statusOf(e) === 409) void reload()
      setCfaCreate({ busy: false, error: errorText(e, 'Couldn’t create the CFA.') })
    }
  }, [caseId, reload, announce])

  // ── The open card (read-first: pencil → draft → ONE Save) ──
  const loaded = state && !state.locked ? state : null
  const saved = loaded?.matter.values ?? null
  const openCard = (card: CardKey): void => {
    if (!loaded || editing) return
    setEditing({ card, opened: loaded.matter.values, base: loaded.matter.updatedAt, draft: loaded.matter.values, override: overrideOf(loaded), recoveries: {} })
    setTyping({}); setFieldErrors({}); setSaveError(null); setConflict(false); bad.reset()
    // Keyboard users land in the first field of the card they opened.
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`#${cardDomId(card)} input:not([type=hidden]), #${cardDomId(card)} select, #${cardDomId(card)} button[role=switch]`)
      el?.focus()
    }, 0)
  }
  const closeCard = (): void => { setEditing(null); setTyping({}); setFieldErrors({}); setSaveError(null); bad.reset() }
  // (Audit fix batch 2, M8) After Save / Cancel, focus goes back to that card's
  // pencil (never to the top of the page); a polite live region says "Saved".
  const [said, setSaid] = useState('')
  const backToPencil = (card: CardKey): void => {
    window.setTimeout(() => document.querySelector<HTMLElement>(`#${cardDomId(card)} [data-edit-pencil]`)?.focus(), 0)
  }
  const cancelCard = (): void => { const c = editing?.card; closeCard(); setSaid(''); if (c) backToPencil(c) }
  const setField = <K extends keyof MatterValues>(k: K, v: MatterValues[K]): void => {
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, [k]: v } } : e))
    setFieldErrors((fe) => { if (!fe[k]) return fe; const next = { ...fe }; delete next[k]; return next })
    setSaveError(null)
  }
  const setOverrideDraft = (o: OverrideDraft): void => setEditing((e) => (e ? { ...e, override: o } : e))
  const setRecovery = (d: SettlementDisbursement, patch: RecoveryPatch): void =>
    setEditing((e) => (e ? { ...e, recoveries: { ...e.recoveries, [d.id]: { ...e.recoveries[d.id], ...patch } } } : e))
  const typingFor = (k: string) => (ok: boolean): void => setTyping((t) => (!!t[k] === !ok ? t : { ...t, [k]: !ok }))

  // Is the open card's draft valid to save?
  const today = loaded?.today ?? ''
  const blocked = ((): boolean => {
    if (!editing || !loaded) return false
    if (bad.any || Object.values(typing).some(Boolean)) return true
    for (const k of CARD_FIELDS[editing.card]) {
      if (NO_FUTURE[k] && futureDateError(NO_FUTURE[k]!, editing.draft[k] as string | null, today)) return true
    }
    if (editing.card === 'costs' && editing.override.on && (editing.override.net == null || editing.override.vat == null || !editing.override.reason.trim())) return true
    if (editing.card === 'ate') {
      for (const [id, r] of Object.entries(editing.recoveries)) {
        const d = loaded.disbursements.find((x) => x.id === id)
        if (d && recoveryBad(d, { ...d, ...r })) return true
      }
    }
    return false
  })()
  const changedRecoveries = (e: Editing, s: Loaded): [string, RecoveryPatch][] => Object.entries(e.recoveries).filter(([id, r]) => {
    const d = s.disbursements.find((x) => x.id === id)
    return !!d && (Object.keys(r) as (keyof RecoveryPatch)[]).some((k) => r[k] !== d[k])
  })
  const cardDirty = !!editing && !!loaded && (
    Object.keys(cardBody(editing)).length > 0
    || (editing.card === 'costs' && overrideChanged(loaded, editing.override))
    || (editing.card === 'ate' && changedRecoveries(editing, loaded).length > 0)
    || bad.any || Object.values(typing).some(Boolean)
  )

  const saveCard = async (): Promise<void> => {
    if (!editing || !loaded || blocked || saving) return
    const e = editing
    const body = cardBody(e)
    setSaving(true); setSaveError(null); setConflict(false)
    try {
      // The whole card is ONE request, only what changed, against the base the
      // draft was opened from. (B4 follow-up) The Costs card (figures + the
      // override) and the ATE card (figures + each recovery) go together to
      // their card route — all-or-nothing on the server — never one request
      // per part. The other cards are figures only (the matter PATCH).
      if (e.card === 'costs' || e.card === 'ate') {
        const recoveries = e.card === 'ate' ? changedRecoveries(e, loaded).map(([id, r]) => ({ id, ...r })) : []
        const override = e.card === 'costs' && overrideChanged(loaded, e.override)
          ? (e.override.on ? { netPence: e.override.net!, vatPence: e.override.vat!, reason: e.override.reason.trim() } : { clear: true as const })
          : undefined
        if (Object.keys(body).length || override || recoveries.length) {
          // (Audit fix batch 1, H4) The version ALWAYS goes — an override-only
          // or recoveries-only save is checked too (someone else's change → 409).
          await saveSettlementCardApi(caseId, e.card, {
            baseUpdatedAt: e.base,
            ...(Object.keys(body).length ? { matter: body } : {}),
            ...(override ? { override } : {}),
            ...(recoveries.length ? { recoveries } : {}),
          })
        }
      } else if (Object.keys(body).length) {
        await patchSettlementMatterApi(caseId, body, e.base)
        // (B2 follow-up) The limitation estimate's capacity note reads the
        // open case's copy of this flag (not a case column, so no case autosave).
        if ('protectedParty' in body) {
          useCasevoStore.setState((st) => (st.currentCase?.id === caseId ? { currentCase: { ...st.currentCase, protectedParty: body.protectedParty === true } } : {}))
        }
      }
    } catch (err) {
      if (statusOf(err) === 409 && codeOf(err) === 'STATEMENT_LOCKED') {
        // A statement locked the figures meanwhile: say so. (Audit fix batch 1,
        // M5) The card stays open READ-ONLY with what was typed (nothing is
        // thrown away) until it is cancelled.
        setLockMessage(errorText(err, 'These figures are locked by a settlement statement.'))
        void refresh()
      } else if (statusOf(err) === 409) setConflict(true)
      else if (statusOf(err) === 400 && fieldOf(err) && fieldOf(err)! in body) {
        setFieldErrors((fe) => ({ ...fe, [fieldOf(err)!]: errorText(err, 'Not saved.') }))
      } else setSaveError(errorText(err, 'Couldn’t save. Check your connection and try again.'))
      setSaving(false)
      return
    }
    // (Audit fix batch 1, Low 13) Saved. Re-reading is separate: if THAT fails
    // the page says so (with Retry) — never "Couldn’t save" for a saved card.
    closeCard()
    setSaving(false)
    setSaid(`${CARD_TITLE[e.card]} saved.`)
    backToPencil(e.card)
    announce('settlementInputs')
    try { await reload() } catch { setLoadError('Saved — but the figures couldn’t be refreshed. Retry to see them.') }
  }

  // ── Preview (saved + the open card's draft + an unsaved CFA / interim) ──
  const effective = useMemo<MatterValues | null>(() => {
    if (!saved) return null
    if (!editing) return saved
    const out: MatterValues = { ...saved }
    for (const k of CARD_FIELDS[editing.card]) (out as unknown as Record<string, unknown>)[k] = editing.draft[k]
    return out
  }, [saved, editing])
  const cfaDirty = !!loaded?.cfa && !!cfaDraft && Object.keys(cfaChanges(cfaEditableOf(loaded.cfa), cfaDraft)).length > 0
  const edits = useMemo<PreviewEdits | null>(() => {
    if (!loaded || !effective) return null
    const e: PreviewEdits = { matter: effective }
    if (loaded.cfa && cfaDraft && Object.keys(cfaChanges(cfaEditableOf(loaded.cfa), cfaDraft)).length) e.cfa = cfaTermsOnly(cfaDraft)
    if (editing?.card === 'costs') {
      const o = editing.override
      if (o.on && o.net != null && o.vat != null) e.baseCostsOverride = { netPence: o.net, vatPence: o.vat }
      else if (!o.on && loaded.matter.baseCostsOverride) e.baseCostsOverride = null
    }
    if (editing?.card === 'ate') {
      // Unsaved recoveries the save would refuse are left out of the preview.
      const dd = Object.fromEntries(Object.entries(editing.recoveries)
        .filter(([id, r]) => { const d = loaded.disbursements.find((x) => x.id === id); return !!d && !recoveryBad(d, { ...d, ...r }) })
        .map(([id, r]) => [id, { recoveredPence: r.recoveredPence, recoveredVatPence: r.recoveredVatPence }]))
      if (Object.keys(dd).length) e.disbursements = dd
    }
    if (interimForm && interimForm.amount != null && interimForm.amount > 0) {
      const list = loaded.interims.map((p) => (p.id === interimForm.id ? interimForm.amount! : p.amountPence))
      e.interimPaymentsPence = interimForm.id ? list : [...list, interimForm.amount]
    }
    return e
  }, [loaded, effective, cfaDraft, editing, interimForm])
  // A figure being changed. A what-if is not one: it never reaches the summary.
  const figureEdits = cardDirty || cfaDirty || (!!interimForm && interimForm.amount != null)
  const unsavedInPreview = figureEdits || trying !== null
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  // The last run without a what-if: the summary falls back on this, never on a what-if.
  const plainPreview = useRef<PreviewResult | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const seq = useRef(0)
  const stmtDraft = stmt?.draft ?? null
  const options = useMemo<PreviewOptions>(() => trying ?? (stmtDraft ? {
    settlementDate: stmtDraft.settlementDate ?? undefined, kind: stmtDraft.kind, retentionPence: stmtDraft.kind === 'INTERIM' ? stmtDraft.retentionPence : 0,
    shortfallWaived: stmtDraft.shortfallWaived, netDestination: stmtDraft.netDestination,
  } : {}), [trying, stmtDraft])
  const key = JSON.stringify({ edits, options, rev })
  useEffect(() => {
    if (!edits) return
    const n = ++seq.current
    setPreviewLoading(true)
    const t = window.setTimeout(() => {
      previewSettlementApi(caseId, { edits, options })
        .then((r) => { if (n === seq.current) { setPreview(r); setPreviewError(null); if (!trying) plainPreview.current = r } })
        .catch((e) => { if (n === seq.current) setPreviewError(errorText(e, 'Couldn’t run the preview.')) })
        .finally(() => { if (n === seq.current) setPreviewLoading(false) })
    }, preview ? 350 : 0)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, caseId])
  // An unsaved card holds the drawer open and says why: nothing typed is lost.
  const [drawerHeld, setDrawerHeld] = useState(false)
  // Stable: the drawer's focus handling runs on open and close only, never while typing.
  const closeNow = useRef<() => void>(() => undefined)
  closeNow.current = (): void => {
    if (cardDirty) { setDrawerHeld(true); return }
    setDrawerHeld(false); setEditing(null); setDrawerOpen(false)
  }
  const closeDrawer = useCallback((): void => closeNow.current(), [])

  // ── Render ──
  const head = <SectionHead title="Settlement" sub="Where things stand, the next step, and the money." />
  if (loadError && !state) return <div>{head}<Banner tone="error">{loadError}</Banner></div>
  if (!state) return <div ref={sectionRef}>{head}<div className={phase === 'wait' ? 'invisible' : ''}><Placeholder twoCol={twoCol} /></div></div>
  if (state.locked) {
    return (
      <div>
        {head}
        <section className={`${CARD} px-6 py-9 text-center`} aria-label="Settlement locked">
          <div className="w-12 h-12 rounded-[13px] bg-casevo-border-soft text-casevo-text-soft grid place-items-center mx-auto mb-3"><BIcon name="lock" size={22} /></div>
          <h3 className="m-0 text-[15px] font-semibold text-casevo-forest">Settlement figures are locked</h3>
          <p className={`${NOTE} mx-auto mt-1 max-w-[440px]`}>{state.reason}</p>
        </section>
      </div>
    )
  }
  const s = state
  const m = effective!
  const lock = s.statementLock ?? null
  // Date-order warnings (amber), from the CURRENT drafts — same rule + wording
  // as the preview's list.
  const dateWarnings = settlementDateWarnings({
    accidentDate: s.accidentDate ?? null,
    cfaSignedOn: cfaDraft?.signedOn ?? null,
    settlementDate: options.settlementDate ?? s.today,
    issuedOn: m.issuedOn, quantumDate: m.quantumDate, cruCertificateDate: m.cruCertificateDate,
    recoveredCostsAgreedOn: m.recoveredCostsAgreedOn,
    interims: s.interims.map((p) => ({ id: p.id, receivedOn: p.receivedOn })),
  })
  // Payee-aware labels: the draft statement's destination (else the
  // capacity's default) and whether a court order is recorded.
  const courtOrderRecorded = stmt
    ? (stmt.draft ? !!stmt.draft.courtOrder : stmt.statements.some((x) => x.status !== 'SUPERSEDED' && !!x.courtOrder))
    : false
  const payee: Payee = {
    statementDestination: stmt?.draft?.netDestination ?? null,
    representativeType: s.representatives?.representativeType ?? stmt?.representativeType ?? null,
    representativeNamed: !!s.representatives?.representativeName.trim(),
    courtOrderRecorded,
  }
  const cfaUnreviewed = !!s.cfa && s.cfa.source === 'MIGRATED' && s.cfa.needsReview && !s.cfa.reviewedAt
  // Open the drawer on a line's card (read-first, unless it can be edited now).
  const openFigures = (target?: MoneyEditTarget): void => {
    setDrawerOpen(true)
    if (!target) return
    if (target === 'interims') { window.setTimeout(() => document.getElementById('settlement-card-interims')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0); return }
    if (!lock && !editing) openCard(target)
  }
  // From the preview (or its breakdown modal): to the CFA card's review box.
  const goToCfa = (): void => {
    const go = (): void => scrollFocus(document.getElementById(CFA_REVIEW_BUTTON_ID) ?? document.getElementById(CFA_CARD_ID))
    if (!drawerOpen) setDrawerOpen(true)
    if (!breakdownOpen) { if (drawerOpen) go(); else window.setTimeout(go, 0); return }
    // From the breakdown modal: focus once it has closed — after its own
    // focus-restore to "Show breakdown", which would otherwise win.
    setBreakdownOpen(false)
    window.setTimeout(go, 0)
  }
  // The statement as issued, with no newer draft: the figures are frozen.
  const issuedNow = stmt && !stmt.draft ? stmt.statements.find((x) => (x.status === 'ISSUED' || x.status === 'APPROVED') && !!x.frozen) ?? null : null
  const asIssued = (st: NonNullable<typeof issuedNow>) => {
    const f = st.frozen!
    const fd = f.damages
    return {
      preview: { ok: true, errors: [], warnings: [], used: f.result.used as PreviewResult['used'], result: f.result } as PreviewResult,
      error: null, loading: false, unsaved: false, frozen: true, empty: null, options: {} as PreviewOptions, cfaUnreviewed: false, firmCapLabel: null,
      heads: { pslaPence: fd?.pslaPence ?? 0, pastLossPence: fd?.pastLossPence ?? 0, futureLossPence: fd?.futureLossPence ?? 0, interestPence: fd?.interestPence ?? 0 },
      payee: { statementDestination: st.netDestination, representativeType: payee.representativeType, representativeNamed: payee.representativeNamed, courtOrderRecorded: !!st.courtOrder },
    }
  }
  // Nothing to state yet: the page's main button is the real next step.
  const damagesSaved = s.matter.values.pslaPence + pastLossTotal(s.matter.values) + s.matter.values.futureLossPence + s.matter.values.interestPence
  const setup: Setup = !s.cfa ? 'noCfa' : damagesSaved === 0 ? 'noDamages' : null
  const previewProps = {
    preview, loading: previewLoading, error: previewError, options, setOptions: setTrying, today: s.today,
    cfaUnreviewed, onReviewCfa: goToCfa,
    heads: { pslaPence: m.pslaPence, pastLossPence: pastLossTotal(m), futureLossPence: m.futureLossPence, interestPence: m.interestPence },
    firmCapLabel: cfaDraft?.totalCapBasisPoints != null ? `${cfaDraft.totalCapBasisPoints / 100}%` : null,
    unsaved: unsavedInPreview,
    // A figure being typed in shows live; until then, a calm line.
    empty: figureEdits ? null : setup === 'noCfa' ? 'No CFA yet, so there’s nothing to work out.' : setup === 'noDamages' ? 'No damages yet, so there’s nothing to work out.' : null,
    payee,
  }
  const editingCard = editing?.card ?? null
  const fig = (card: CardKey): FigProps => ({
    caseId, s, m, setField, fieldErrors, dateWarnings, typingFor, track: bad.track,
    editing: editingCard === card,
    // (Audit fix batch 1, M5) Locked while this card was open: what was typed
    // stays on screen, read-only, with a note — Cancel closes it.
    readOnly: editingCard === card && !!lock,
    locked: !!lock || !!issuedNow,
    pencil: lock || issuedNow ? null : (
      <EditPencil title={CARD_TITLE[card]} onClick={() => { setSaid(''); openCard(card) }} disabled={!!editingCard}
        disabledReason={editingCard ? `Save or cancel the ${CARD_TITLE[editingCard]} card first.` : undefined} describedBy={ONE_CARD_ID} />
    ),
    footer: editingCard !== card ? null : lock
      ? <LockedFooter version={lock.version} stage={lock.stage} onCancel={cancelCard} />
      : <EditFooter blocked={blocked} saving={saving} error={saveError} onCancel={cancelCard} onSave={() => { void saveCard() }} />,
  })
  // (Audit fix batch 1, M5) "Mark as prepared" locks the figures, so it waits
  // while anything on this page is unsaved (the statement panel shows why).
  const stagesDirty = !!loaded?.cfa && JSON.stringify(stageText) !== JSON.stringify(stageDraftOf(loaded.cfa.stages))
  const interimDirty = !!interimForm && (interimForm.amount != null || !!interimForm.receivedOn || !!interimForm.payer.trim() || !!interimForm.note.trim())
  const figuresUnsaved = trying ? 'Go back to the saved figures first: these are only a what-if.'
    : cardDirty && editing ? `Save or cancel the ${CARD_TITLE[editing.card]} card first — preparing locks the figures.`
    : cfaDirty || stagesDirty ? 'Save or cancel your CFA changes first — preparing locks the figures.'
      : interimDirty ? 'Save or cancel the interim payment first — preparing locks the figures.'
        : null
  // (Follow-ups) "Send back for changes" here opens the SAME reason box as
  // the statement's own button, right where it was clicked (no scroll), and
  // sends it back from here; the statement panel re-reads (announce).
  const sendBackHere = async (reason: string): Promise<boolean> => {
    if (!lock) return false
    setSendingBack({ busy: true, error: null })
    try {
      await sendBackStatementApi(caseId, lock.statementId, reason)
      setLockMessage(null)
      await reload(); void loadStmt()
      announce('statement')
      setSendingBack(null)
      return true
    } catch (e) {
      setSendingBack({ busy: false, error: errorText(e, 'Couldn’t send it back. Please try again.') })
      return false
    }
  }
  // B4 — a statement block reason's "fix it" button: each target DOES it.
  const fixStatement = (target: FixTarget): void => {
    if (target === 'settlementWording' || target === 'esignSettings') {
      // (B4c 1b) "Approve the wording" / "Open E-signature settings" → Admin → E-signature.
      const section = target === 'esignSettings' ? 'esign' : 'settlementWording'
      try { localStorage.setItem('casevo_admin_section', section) } catch { /* storage off */ }
      void navigateTo({ view: 'admin', params: { section } })
    } else if (target === 'clientDetails') void navigateTo({ view: 'caseDetail', caseId, caseSection: 'clientDetails' })
    else if (target === 'litigationFriend') goToLitigationFriend(caseId)
    else if (target === 'createCfa') {
      setDrawerOpen(true)
      window.setTimeout(() => scrollFocus(document.getElementById(CFA_CARD_ID), 'start'), 0)
      if (!s.cfa) void createCfa()
    } else if (target === 'cfa') {
      setDrawerOpen(true)
      if (!s.cfa) { window.setTimeout(() => scrollFocus(document.getElementById(CFA_CARD_ID), 'start'), 0); return }
      if (cfaUnreviewed) { window.setTimeout(() => scrollFocus(document.getElementById(CFA_REVIEW_BUTTON_ID)), 0); return }
      window.setTimeout(() => document.getElementById(CFA_CARD_ID)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 0)
      if (!lock) setCfaOpenReq((n) => n + 1)
    } else if (target === 'settlementInputs') {
      setDrawerOpen(true)
      // The figures start with Damages: open it (unless locked / one is open).
      if (!lock && !editing) openCard('damages')
    } else if (target === 'ateCard') {
      setDrawerOpen(true)
      if (!lock && !editing) openCard('ate')
    } else if (target === 'billing') {
      // (Audit B2, M3) An unpaid interim bill: the case's Billing section (a history step).
      const st = useCasevoStore.getState()
      st.pushViewHistory()
      st.setActiveCaseSection('billing')
    } else if (target === 'courtOrder') scrollFocus(document.getElementById('court-order') ?? document.getElementById('settlement-statement'))
    // Statement-internal targets (prepare, approve, dates …) are handled by
    // the statement panel itself; anything else lands on the statement.
    else scrollFocus(document.getElementById('settlement-statement'), 'start')
  }
  const afterInterim = async (): Promise<void> => { await reload(); announce('settlementInputs') }
  // The sentence's name.
  const clientFirstName = currentCaseClient?.forename?.trim() || null
  // The one money summary. A draft: its own server calculation (live while a
  // figure is being edited). Issued, no draft: the frozen figures, never re-run.
  const moneyOf = (d: CaseStatements | null): { node: React.ReactNode; now: { netPence: number; deductionsPence: number; ok: boolean } | null } => {
    const current = d?.statements.find((x) => x.status === 'ISSUED' || x.status === 'APPROVED') ?? null
    const issued = !d?.draft && current?.frozen ? current : null
    const own = d?.draft?.calculation ?? null
    // Never a what-if: with one showing, the saved figures stay here.
    const shown: PreviewResult | null = issued ? null : !figureEdits && own ? own : trying ? own ?? plainPreview.current : preview
    const frozenProps = issued ? asIssued(issued) : null
    const r = (frozenProps ? frozenProps.preview : shown)?.result ?? null
    const who = clientFirstName ?? 'The client'
    const adult = (r?.capacity ?? 'ADULT') === 'ADULT'
    return {
      now: r ? { netPence: r.figures.netToClientPence, deductionsPence: r.figures.totalDeductionsPence, ok: frozenProps ? true : !!shown?.ok } : null,
      node: (
        <SettlementPreview {...previewProps} {...(frozenProps ?? { preview: shown, unsaved: figureEdits })}
          headline={adult ? `${who} ${frozenProps ? 'gets' : 'would get'}` : undefined} heldPence={issued?.position?.heldPence ?? null}
          onEdit={lock || frozenProps ? undefined : openFigures} onOpenFigures={() => openFigures()}
          hideCodes={d?.draft?.advisory.map((n) => n.code)}
          worked={d?.draft?.deductionsBreakdown ? <DeductionsBreakdown breakdown={d.draft.deductionsBreakdown} /> : undefined} />
      ),
    }
  }
  const whatIfBanner = trying ? (
    <ActionBox ariaLabel="What-if" tone="warn" variant="slim" title="Trying different figures — not saved"
      actions={[{ label: 'Back to the saved figures', kind: 'link', onClick: () => setTrying(null) }]} />
  ) : null

  return (
    <div ref={sectionRef} data-settlement-root className="text-casevo-forest">
      {head}
      {loadError && <div className="mb-3"><Banner tone="error" action={<button type="button" className={B_SECONDARY_SM} onClick={() => { void refresh() }}>Retry</button>}>{loadError}</Banner></div>}
      {conflict && (
        <div className="mb-3">
          <Banner tone="error" action={<button type="button" className={B_SECONDARY_SM} onClick={() => { setConflict(false); closeCard(); void load() }}>Reload</button>}>
            Someone else changed these settlement details since you opened them, so your change wasn’t saved. Reload to see theirs, then make your change again.
          </Banner>
        </div>
      )}
      <UnsavedGuard dirty={cardDirty && !saving} />
      <span role="status" className="sr-only" data-settlement-saved="">{said}</span>
      {whatIfBanner && <div className="mb-4">{whatIfBanner}</div>}
      <StatementPanel caseId={caseId} onFix={fixStatement} figuresUnsaved={figuresUnsaved} clientName={clientFirstName} twoCol={twoCol} moneyOf={moneyOf} onOpenFigures={() => openFigures()} editingFigures={!!editingCard}
        tryingFigures={trying !== null} setup={setup} cfaCreate={{ run: () => { void createCfa() }, busy: cfaCreate.busy, error: cfaCreate.error }} />
      <FiguresDrawer open={drawerOpen} onClose={closeDrawer}
        foot={<SettlementBottomBar {...previewProps} {...(issuedNow ? asIssued(issuedNow) : {})} open={breakdownOpen} setOpen={setBreakdownOpen} />}>
        {whatIfBanner}
        {/* (D12) Someone else changed the settlement while a card is open: its draft stays. */}
        {editingCard && <LiveUpdateNote notice={liveEdit.notice} dirty={cardDirty}
          onReload={() => { closeCard(); liveEdit.clear(); void load() }} />}
        {/* (M8) Why the other pencils are off (they point here), and why it won't close. */}
        {editingCard && !lock && <p id={ONE_CARD_ID} className={`${NOTE} m-0`} role={drawerHeld ? 'status' : undefined}>Save or cancel the {CARD_TITLE[editingCard]} card before editing another{drawerHeld ? ' or closing the figures' : ''}.</p>}
        <div className="flex flex-col gap-4 min-w-0" data-settlement-figures>
          {issuedNow && !lock && <ActionBox ariaLabel="Figures frozen" title="Figures frozen" tone="info" variant="slim" icon="lock" message={`Statement ${issuedNow.number ?? ''} v${issuedNow.version} is issued. To change a figure, correct it.`} />}
          {lock ? (
            // (Audit fix batch 2) Pure information with no real step: the slim line.
            <>
              <ActionBox ariaLabel="Figures locked" title="Figures locked" tone="info" variant="slim" icon="lock"
                message={`Statement v${lock.version} is ${lock.stage === 'PREPARED' ? 'prepared' : 'approved'}.`}
                actions={[{ label: 'Send back for changes', kind: 'link', arrow: true, onClick: () => setSendingBack({ busy: false, error: null }) }]} />
              {lockMessage && <Banner tone="error">{lockMessage}</Banner>}
            </>
          ) : lockMessage ? <Banner tone="error">{lockMessage}</Banner> : null}
          {sendingBack && lock && <SendBackDialog busy={sendingBack.busy} error={sendingBack.error} onClose={() => setSendingBack(null)} onSend={sendBackHere} />}
          <CfaPanel caseId={caseId} cfa={s.cfa} draft={cfaDraft} setDraft={setCfaDraft} stageText={stageText} setStageText={setStageText} onChanged={reload}
            accidentDate={s.accidentDate ?? null} today={s.today} stageReached={m.stageReached} locked={!!lock || !!issuedNow}
            create={{ run: () => { void createCfa() }, busy: cfaCreate.busy, error: cfaCreate.error }}
            openRequest={cfaOpenReq} onSaved={() => announce('cfa')} />
          {dateWarnings.some((w) => w.code === 'NO_ACCIDENT_DATE') && <p className={`${NOTE} m-0 rounded-[10px] bg-casevo-input px-[12px] py-[8px]`}>{NO_ACCIDENT_DATE_NOTE}</p>}
          {/* The card being edited comes first, with the live net at the drawer's foot. */}
          <div className={editingCard === 'damages' ? 'order-first' : ''}><DamagesCard {...fig('damages')} /></div>
          <div className={editingCard === 'pastLoss' ? 'order-first' : ''}><PastLossCard {...fig('pastLoss')} /></div>
          <div className={editingCard === 'cru' ? 'order-first' : ''}><CruCard {...fig('cru')} /></div>
          <div className={editingCard === 'costs' ? 'order-first' : ''}><CostsCard {...fig('costs')} override={editing?.override ?? null} setOverride={setOverrideDraft} /></div>
          <div className={editingCard === 'ate' ? 'order-first' : ''}><AteDisbCard {...fig('ate')} draft={editing?.card === 'ate' ? editing.recoveries : {}} setRecovery={setRecovery} /></div>
          <InterimCard caseId={caseId} interims={s.interims} form={interimForm} setForm={setInterimForm} onSaved={afterInterim} dateWarnings={dateWarnings} today={s.today} locked={!!lock}
            formWarnings={(receivedOn) => settlementDateWarnings({
              accidentDate: s.accidentDate ?? null, cfaSignedOn: cfaDraft?.signedOn ?? null, settlementDate: options.settlementDate ?? s.today,
              issuedOn: null, quantumDate: null, cruCertificateDate: null, recoveredCostsAgreedOn: null, interims: [{ id: 'form', receivedOn }],
            }).filter((w) => w.field === 'interim:form')} />
          <div className={editingCard === 'court' ? 'order-first' : ''}><CourtCard {...fig('court')} /></div>
          <div className={editingCard === 'client' ? 'order-first' : ''}><ClientCard {...fig('client')} onReload={afterInterim} /></div>
          {/* (B4 follow-ups) The litigation friend + deputy / attorney live in
              Client details → Litigation friend — a one-line summary here. */}
          {(m.protectedParty || s.client.childToday) && <LitigationFriendSummary caseId={caseId} r={s.representatives} />}
          {!issuedNow && <PreviewOptionsFold options={options} setOptions={setTrying} today={s.today} used={preview?.used ?? null} changed={trying !== null} onReset={() => setTrying(null)} />}
        </div>
      </FiguresDrawer>
    </div>
  )
}

// ── Figures cards (option A: stacked cards, subtotal in each header) ──
type FigProps = {
  caseId: string; s: Loaded; m: MatterValues
  setField: <K extends keyof MatterValues>(k: K, v: MatterValues[K]) => void
  fieldErrors: Partial<Record<keyof MatterValues, string>>
  dateWarnings: DateWarning[]
  typingFor: (k: string) => (ok: boolean) => void
  track: (key: string) => (bad: boolean) => void
  // This card's draft is open (else: the read view).
  editing: boolean
  // (Audit fix batch 1, M5) Open, but the figures were locked meanwhile.
  readOnly?: boolean
  // (Audit fix batch 2) A statement locks the figures.
  locked?: boolean
  pencil: React.ReactNode | null
  footer: React.ReactNode | null
}
// The dates that can't be in the future — with the server's own labels, so
// the page and a 400 say the same thing.
const NO_FUTURE: Partial<Record<keyof MatterValues, string>> = { issuedOn: 'Issued date', recoveredCostsAgreedOn: 'Costs agreed date', cruCertificateDate: 'CRU certificate date' }
// Under a date: the server's refusal, else "can't be in the future" for the
// dates that are blocked, else the amber date-order warnings.
const dateNoteFor = (p: FigProps, k: keyof MatterValues): React.ReactNode => {
  if (p.fieldErrors[k]) return <span className={FIELD_ERROR} role="alert">{p.fieldErrors[k]}</span>
  const future = p.editing && NO_FUTURE[k] ? futureDateError(NO_FUTURE[k]!, p.m[k] as string | null, p.s.today) : null
  if (future) return <span className={FIELD_ERROR} role="alert">{future}</span>
  const ws = p.dateWarnings.filter((w) => w.field === k)
  return ws.length ? ws.map((w) => <span key={w.code} className="block text-[12px] text-casevo-warn mt-[4px]" role="status">{w.message}</span>) : null
}
const errNote = (p: FigProps, k: keyof MatterValues): React.ReactNode => (p.fieldErrors[k] ? <span className={FIELD_ERROR} role="alert">{p.fieldErrors[k]}</span> : null)
// A money figure: the box while editing, the saved amount otherwise.
const Money: React.FC<{ p: FigProps; k: keyof MatterValues; label: string; hint?: React.ReactNode; warning?: React.ReactNode; allowBlank?: boolean }> = ({ p, k, label, hint, warning, allowBlank }) => {
  const v = p.m[k] as number | null
  if (!p.editing) return <ReadRow label={label} hint={hint} note={warning ? <span className="block text-[12px] text-casevo-warn mt-[4px]" role="status">{warning}</span> : null}>{v == null ? <span className={MUTED}>None</span> : money(v)}</ReadRow>
  return (
    <PenceField layout="row" label={label} hint={hint} warning={warning} allowBlank={allowBlank} pence={v}
      onPence={(x) => p.setField(k, (allowBlank ? x : x ?? 0) as never)} error={p.fieldErrors[k] ?? null} onBadChange={p.track(k)} />
  )
}
const DateRow: React.FC<{ p: FigProps; k: keyof MatterValues; label: string; hint?: string; allowFuture?: boolean }> = ({ p, k, label, hint, allowFuture }) => {
  const v = p.m[k] as string | null
  if (!p.editing) return <ReadRow label={label} hint={hint} note={dateNoteFor(p, k)}>{v ? dmy(v) : <span className={MUTED}>Not recorded</span>}</ReadRow>
  return (
    <FieldRow label={label} hint={hint} note={dateNoteFor(p, k)}>
      <DateInput value={v} allowFuture={allowFuture} onChange={(x) => p.setField(k, x as never)} ariaLabel={label} onValidityChange={p.typingFor(k)} />
    </FieldRow>
  )
}
const TextRow: React.FC<{ p: FigProps; k: 'ateProvider' | 'court'; label: string; placeholder?: string }> = ({ p, k, label, placeholder }) => {
  const v = p.m[k]
  if (!p.editing) return <ReadRow label={label} note={errNote(p, k)}>{v.trim() ? v : <span className={MUTED}>Not recorded</span>}</ReadRow>
  return (
    <FieldRow label={label} note={errNote(p, k)}>
      <input aria-label={label} className={`${INPUT} w-[220px]`} value={v} maxLength={200} placeholder={placeholder} onChange={(e) => p.setField(k, e.target.value)} />
    </FieldRow>
  )
}
const Divider: React.FC = () => <div className="my-[6px] border-t border-casevo-border-soft" />
// The card shell: pencil (read view) or "Editing" + the Save / Cancel footer.
const cardChrome = (p: FigProps, card: CardKey): { id: string; editing: boolean; readOnly: boolean; footer: React.ReactNode; right: React.ReactNode } => ({
  id: cardDomId(card),
  editing: p.editing,
  readOnly: !!p.readOnly,
  footer: p.footer,
  right: p.editing ? <span className="text-[12.5px] text-casevo-text-soft">{p.readOnly ? 'Locked' : 'Editing'}</span> : p.pencil,
})

const DamagesCard: React.FC<FigProps> = (p) => {
  const { m } = p
  const total = m.pslaPence + pastLossTotal(m) + m.futureLossPence + m.interestPence
  return (
    <FigureCard icon="scale" title="Damages" sub="Agreed or awarded, by head" total={money(total)} totalLabel="total" {...cardChrome(p, 'damages')}>
      <Money p={p} k="pslaPence" label="General damages (PSLA)" hint="Pain, suffering and loss of amenity" />
      <Money p={p} k="futureLossPence" label="Future loss" hint="Never counted in the 25% cap" />
      <Money p={p} k="interestPence" label="Interest" />
      <DateRow p={p} k="quantumDate" label="Quantum date" allowFuture />
    </FigureCard>
  )
}

const PastLossCard: React.FC<FigProps> = (p) => (
  <FigureCard icon="history" title="Past loss" sub="Losses up to settlement — split so CRU comes only from the matching loss" total={money(pastLossTotal(p.m))} totalLabel="total" {...cardChrome(p, 'pastLoss')}>
    <Money p={p} k="pastLossEarningsPence" label="Loss of earnings" />
    <Money p={p} k="pastLossCarePence" label="Cost of care" />
    <Money p={p} k="pastLossMobilityPence" label="Loss of mobility" />
    <Money p={p} k="pastLossOtherPence" label="Other past loss" hint="Travel, repairs and other expenses (no CRU)" />
  </FigureCard>
)

const CruCard: React.FC<FigProps> = (p) => {
  const { m } = p
  // Like-for-like: each CRU head against its MATCHING past-loss head. CRU
  // above its head isn't deducted (the head counts as £0) — warned, not an error.
  const cruHeadWarning = (cru: number, loss: number, what: string): string | null =>
    (cru > loss ? `CRU on ${what} exceeds the loss (${money(loss)}); that head counts as £0.` : null)
  const cruTotal = m.cruPastLossEarningsPence + m.cruPastCarePence + m.cruPastMobilityPence
  // What's actually offset: each head capped at its matching past loss (the
  // server's cruApplied rule).
  const cruApplied = Math.min(m.cruPastLossEarningsPence, m.pastLossEarningsPence) + Math.min(m.cruPastCarePence, m.pastLossCarePence) + Math.min(m.cruPastMobilityPence, m.pastLossMobilityPence)
  return (
    <FigureCard icon="shield" title="CRU" sub="Benefits repaid to the DWP, from the matching loss" total={money(cruApplied)} totalLabel="applied" {...cardChrome(p, 'cru')}>
      <Money p={p} k="cruPastLossEarningsPence" label="Loss of earnings" warning={cruHeadWarning(m.cruPastLossEarningsPence, m.pastLossEarningsPence, 'loss of earnings')} />
      <Money p={p} k="cruPastCarePence" label="Cost of care" warning={cruHeadWarning(m.cruPastCarePence, m.pastLossCarePence, 'cost of care')} />
      <Money p={p} k="cruPastMobilityPence" label="Loss of mobility" warning={cruHeadWarning(m.cruPastMobilityPence, m.pastLossMobilityPence, 'loss of mobility')} />
      <DateRow p={p} k="cruCertificateDate" label="Certificate date" />
      {cruTotal > 0 && (cruApplied === cruTotal
        ? <p className={`${NOTE} mt-[6px] mb-0`}>CRU in total: <b className="font-semibold text-casevo-forest">{money(cruTotal)}</b> (taken off past loss in the cap base)</p>
        : <p className={`${NOTE} mt-[6px] mb-0`}>CRU: <b className="font-semibold text-casevo-forest">{money(cruTotal)}</b> on the certificate · <b className="font-semibold text-casevo-forest">{money(cruApplied)}</b> applied (a head can’t go below £0)</p>)}
    </FigureCard>
  )
}

// Costs: base costs (recorded time or an override with a reason), what the
// other side paid, counsel's success fee. Subtotal = the shortfall. The
// override is part of this card's edit (saved WITH the figures in the card's
// one all-or-nothing request; audited on its own).
const CostsCard: React.FC<FigProps & { override: OverrideDraft | null; setOverride: (o: OverrideDraft) => void }> = (p) => {
  const { m, s, override, setOverride } = p
  const [showTime, setShowTime] = useState(false)
  const saved = s.matter.baseCostsOverride
  const b = s.baseCosts
  const reasonId = 'st-override-reason'
  const o = p.editing ? override : null
  const baseNet = o ? (o.on ? o.net ?? 0 : b.netPence) : saved ? saved.netPence : b.netPence
  const baseVat = o ? (o.on ? o.vat ?? 0 : b.vatPence) : saved ? saved.vatPence : b.vatPence
  const interim = s.interimCostsPaid ?? { netPence: 0, vatPence: 0 }
  const paid = interim.netPence + interim.vatPence
  const position = baseNet + baseVat - m.recoveredCostsPence - m.recoveredCostsVatPence - paid
  const shortfall = Math.max(0, position)
  const credit = Math.min(paid, Math.max(0, -position))
  return (
    <FigureCard icon="bill" title="Costs" sub="The firm’s costs and what the other side paid" total={money(credit > 0 ? credit : shortfall)} totalLabel={credit > 0 ? 'credited to the client' : 'shortfall'} {...cardChrome(p, 'costs')}>
      {!o ? (
        <>
          <ReadRow label="Base costs (net)" hint={saved ? <>Overridden · “{saved.reason}”</> : `Recorded time — ${b.entryCount} ${b.entryCount === 1 ? 'entry' : 'entries'} at each entry’s rate`}>{money(baseNet)}</ReadRow>
          <ReadRow label="VAT on base costs">{money(baseVat)}</ReadRow>
          {paid > 0 && <ReadRow label="Already paid on interim bills" hint={`${money(interim.netPence)} + ${money(interim.vatPence)} VAT. It still counts as base costs for the success fee.`}>{money(paid)}</ReadRow>}
        </>
      ) : (
        <div className="my-[4px] rounded-[12px] border border-casevo-border p-[14px]">
          <FieldRow label="Override the recorded time" hint={`Recorded time: ${money(b.netPence)} + ${money(b.vatPence)} VAT. Changing it is recorded in the audit log.`}>
            <SettingsSwitch checked={o.on} label="Override the recorded time" onChange={() => setOverride(o.on
              ? { ...o, on: false }
              : { on: true, net: saved?.netPence ?? b.netPence, vat: saved?.vatPence ?? b.vatPence, reason: saved?.reason ?? '' })} />
          </FieldRow>
          {o.on && (
            <>
              <PenceField layout="row" label="Base costs (net)" pence={o.net} allowBlank onPence={(v) => setOverride({ ...o, net: v })} onBadChange={p.track('overrideNet')}
                error={o.net == null ? 'Enter the base costs.' : null} />
              <PenceField layout="row" label="VAT" hint={o.net != null ? `20% would be ${money(standardVatPence(o.net))}` : undefined} pence={o.vat} allowBlank onPence={(v) => setOverride({ ...o, vat: v })} onBadChange={p.track('overrideVat')}
                error={o.vat == null ? 'Enter the VAT (0 if none).' : null} />
              <label className={`${LABEL} mt-[8px]`} htmlFor={reasonId}>Reason (required)</label>
              <textarea id={reasonId} className={`${INPUT} h-auto min-h-[60px] py-2 ${!o.reason.trim() ? '!border-casevo-danger' : ''}`} value={o.reason} maxLength={1000} aria-invalid={!o.reason.trim() || undefined}
                onChange={(e) => setOverride({ ...o, reason: e.target.value })} placeholder="e.g. Costs agreed with the defendant at a fixed figure" />
              {/* (Audit fix batch 1, Low 13) Shown, not announced: it appears the
                  moment the override is turned on, before anything is typed. */}
              {!o.reason.trim() && <span className={FIELD_ERROR}>Say why the recorded time isn’t right.</span>}
            </>
          )}
          {!o.on && saved && <p className={`${NOTE} mt-[6px] mb-0`}>Saving removes the override and goes back to the recorded time.</p>}
        </div>
      )}
      {b.byFeeEarner.length > 0 && (
        <div className="py-[4px]">
          <button type="button" className={B_GHOST_SM} aria-expanded={showTime} onClick={() => setShowTime((x) => !x)}>
            {showTime ? 'Hide recorded time' : `Recorded time: ${money(b.netPence)} + ${money(b.vatPence)} VAT`}
          </button>
        </div>
      )}
      {showTime && (
        <table className="w-full border-collapse mt-[4px] mb-[6px] text-[13px]">
          <thead><tr><th className={TH}>Fee earner</th><th className={`${TH} text-right`}>Entries</th><th className={`${TH} text-right`}>Time</th><th className={`${TH} text-right`}>Net</th></tr></thead>
          <tbody>
            {b.byFeeEarner.map((r) => (
              <tr key={r.userId}>
                <td className={TD}>{r.name}</td>
                <td className={`${TD} text-right tabular-nums`}>{r.entryCount}</td>
                <td className={`${TD} text-right tabular-nums`}>{fmtDuration(r.seconds)}</td>
                <td className={`${TD} text-right tabular-nums`}>{money(r.netPence)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Divider />
      <Money p={p} k="recoveredCostsPence" label="Recovered from the other side (net)" />
      <Money p={p} k="recoveredCostsVatPence" label="VAT on recovered costs" />
      <DateRow p={p} k="recoveredCostsAgreedOn" label="Costs agreed on" />
      <Money p={p} k="counselSuccessFeePence" label="Counsel’s success fee" hint="Only passed on if the CFA says so" />
    </FigureCard>
  )
}

// ATE insurance + disbursement recoveries. Subtotal = what's unrecovered.
const AteDisbCard: React.FC<FigProps & { draft: DisbDraft; setRecovery: (d: SettlementDisbursement, patch: RecoveryPatch) => void }> = (p) => {
  const { m, s, draft, setRecovery } = p
  const rows = s.disbursements
  const unrecoveredDisb = rows.reduce((a, d) => {
    // (Audit batch 3) Already billed and paid on an interim bill → not counted (as in the deductions).
    if (d.status === 'ANTICIPATED' || d.alreadyBilledOn) return a
    const x = { ...d, ...draft[d.id] }
    return a + Math.max(0, x.amountPence + x.vatPence - x.recoveredPence - x.recoveredVatPence)
  }, 0)
  const ateLeft = Math.max(0, m.atePremiumPence - m.ateRecoveredPence)
  return (
    <FigureCard icon="umbrella" title="ATE & disbursements" sub="Insurance and expenses not recovered" total={money(ateLeft + unrecoveredDisb)} totalLabel="unrecovered" {...cardChrome(p, 'ate')}>
      <TextRow p={p} k="ateProvider" label="ATE provider" />
      <Money p={p} k="atePremiumPence" label="ATE premium" />
      <Money p={p} k="ateRecoveredPence" label="ATE premium recovered" />
      <Money p={p} k="ateCommissionPence" label="Commission to the firm" hint="Blank if none" allowBlank />
      {p.editing ? (
        <FieldRow label="Commission disclosed to the client" hint="Any commission the firm receives must be disclosed">
          <SettingsSwitch checked={m.ateCommissionDisclosed} label="ATE commission disclosed" onChange={() => p.setField('ateCommissionDisclosed', !m.ateCommissionDisclosed)} />
        </FieldRow>
      ) : <ReadRow label="Commission disclosed to the client">{m.ateCommissionDisclosed ? 'Yes' : 'No'}</ReadRow>}
      <Divider />
      {rows.length === 0 ? <p className={`${NOTE} my-[6px]`}>No disbursements on this case (add them in Billing).</p> : rows.map((d) => {
        const x = { ...d, ...draft[d.id] }
        const gross = d.amountPence + d.vatPence
        const left = d.status === 'ANTICIPATED' ? null : Math.max(0, gross - x.recoveredPence - x.recoveredVatPence)
        // (Audit batch 3) Already billed on a settled interim bill: muted, not
        // counted — it isn't part of these deductions. (4 Nov audit) Its recovery
        // can still be recorded while editing: money the other side also paid
        // comes back to the client as a credit.
        if (d.alreadyBilledOn && !p.editing) return (
          <div key={d.id} className="py-[8px] border-t border-casevo-border-soft first:border-t-0 text-casevo-text-soft">
            <div className="flex flex-wrap items-baseline gap-x-[10px]">
              <span className="text-[13.5px]">{d.description || categoryLabel(d.category)}</span>
              <span className="text-[12px]">{money(d.amountPence)}{d.vatPence ? ` + ${money(d.vatPence)} VAT` : ''}</span>
              <span className="ml-auto text-[12.5px]">Already billed on <b className="font-semibold font-mono">{d.alreadyBilledOn}</b> · not counted</span>
            </div>
            {(x.recoveredPence > 0 || x.recoveredVatPence > 0) && <p className={`${NOTE} m-0 mt-[2px]`}>{money(x.recoveredPence + x.recoveredVatPence)} recovered from the other side</p>}
          </div>
        )
        return (
          <div key={d.id} className="py-[8px] border-t border-casevo-border-soft first:border-t-0">
            <div className="flex flex-wrap items-baseline gap-x-[10px]">
              <span className="text-[13.5px] text-casevo-forest">{d.description || categoryLabel(d.category)}</span>
              <span className="text-[12px] text-casevo-text-soft">{money(d.amountPence)}{d.vatPence ? ` + ${money(d.vatPence)} VAT` : ''}{d.status === 'ANTICIPATED' ? ' · not paid yet (not counted)' : ''}</span>
              {d.alreadyBilledOn
                ? <span className="ml-auto text-[12.5px] text-casevo-text-soft">Already billed on <b className="font-semibold font-mono">{d.alreadyBilledOn}</b></span>
                : left != null && <span className="ml-auto text-[12.5px] tabular-nums">Unrecovered <b className="font-semibold">{money(left)}</b></span>}
            </div>
            {!p.editing ? (
              <p className={`${NOTE} m-0 mt-[2px]`}>
                {RECOVERY_LABEL[x.recoveryStatus]}{x.recoveredPence || x.recoveredVatPence ? ` · ${money(x.recoveredPence)}${x.recoveredVatPence ? ` + ${money(x.recoveredVatPence)} VAT` : ''} recovered` : ''}
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-[12px] mt-[6px]">
                <div className="min-w-0">
                  <label className={LABEL} htmlFor={`st-rec-${d.id}`}>Recovery</label>
                  <select id={`st-rec-${d.id}`} className={INPUT} value={x.recoveryStatus} onChange={(e) => {
                    const st = e.target.value as RecoveryStatus
                    // "Recovered" fills in the full amount; "Not recoverable" clears it.
                    if (st === 'RECOVERED') setRecovery(d, { recoveryStatus: st, recoveredPence: d.amountPence, recoveredVatPence: d.vatPence })
                    else if (st === 'IRRECOVERABLE') setRecovery(d, { recoveryStatus: st, recoveredPence: 0, recoveredVatPence: 0 })
                    else setRecovery(d, { recoveryStatus: st })
                  }}>
                    {(Object.keys(RECOVERY_LABEL) as RecoveryStatus[]).map((k) => <option key={k} value={k}>{RECOVERY_LABEL[k]}</option>)}
                  </select>
                </div>
                <PenceField label="Recovered (net)" pence={x.recoveredPence} onPence={(v) => setRecovery(d, { recoveredPence: v ?? 0 })} onBadChange={p.track(`rec:${d.id}`)}
                  error={x.recoveredPence > d.amountPence ? `More than the net amount (${money(d.amountPence)}).` : null} />
                <PenceField label="Recovered VAT" pence={x.recoveredVatPence} onPence={(v) => setRecovery(d, { recoveredVatPence: v ?? 0 })} onBadChange={p.track(`recVat:${d.id}`)}
                  error={x.recoveredVatPence > d.vatPence ? `More than its VAT (${money(d.vatPence)}).` : null} />
              </div>
            )}
          </div>
        )
      })}
    </FigureCard>
  )
}

// ── Interim payments ─────────────────────────────────────────
// The list is the read view; Add / Edit / Remove per row (audited). Locked
// while a statement is prepared / approved.
type InterimForm = { id: string | null; amount: number | null; receivedOn: string | null; payer: string; note: string }
const REFRESH_FAILED = 'Saved — but the list couldn’t be refreshed. Reload the page to see it.'
const InterimCard: React.FC<{
  caseId: string; interims: InterimPayment[]; form: InterimForm | null; setForm: (f: InterimForm | null) => void; onSaved: () => Promise<void>
  dateWarnings: DateWarning[]; today: string; locked?: boolean
  formWarnings: (receivedOn: string | null) => DateWarning[]
}> = ({ caseId, interims, form, setForm, onSaved, dateWarnings, today, formWarnings, locked }) => {
  // Money already received can't be dated in the future (blocked here AND on the server).
  const formFuture = form ? futureDateError('The received date', form.receivedOn, today) : null
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [del, setDel] = useState<InterimPayment | null>(null)
  // The Received on box holds a half-typed / invalid date (Save waits).
  const [dateTyping, setDateTyping] = useState(false)
  // (Audit M11) The amount box holds text that isn't a valid amount.
  const bad = useBadFields()
  useEffect(() => { if (!form) setDateTyping(false) }, [form])
  const total = interims.reduce((a, p) => a + p.amountPence, 0)
  const valid = !!form && form.amount != null && form.amount > 0 && !!form.receivedOn && !formFuture && !dateTyping && !bad.any
  // Dirty only once it differs from what the form opened with.
  const orig = form?.id ? interims.find((p) => p.id === form.id) : null
  const opened = orig ? { amount: orig.amountPence, receivedOn: orig.receivedOn, payer: orig.payer, note: orig.note } : { amount: null, receivedOn: null, payer: '', note: '' }
  const dirty = !!form && (dateTyping || bad.any || form.amount !== opened.amount || form.receivedOn !== opened.receivedOn || form.payer !== opened.payer || form.note !== opened.note)
  const openForm = (f: InterimForm): void => { setError(null); bad.reset(); setForm(f) }
  const save = async (): Promise<void> => {
    if (!form || !valid) return
    setBusy(true); setError(null)
    try {
      const body = { amountPence: form.amount!, receivedOn: form.receivedOn!, payer: form.payer, note: form.note }
      if (form.id) await updateInterimApi(caseId, form.id, body)
      else await createInterimApi(caseId, body)
    } catch (e) { setError(errorText(e, 'Couldn’t save the payment.')); setBusy(false); return }
    // (Audit fix batch 1, Low 13) Saved: a failed re-read is said as such.
    setForm(null)
    try { await onSaved() } catch { setError(REFRESH_FAILED) } finally { setBusy(false) }
  }
  return (
    <FigureCard icon="pound" title="Interim payments" sub="Damages already paid before settlement" total={money(total)} totalLabel={`${interims.length} ${interims.length === 1 ? 'payment' : 'payments'}`} id="settlement-card-interims" editing={!!form}>
      {interims.map((p) => (
        <div key={p.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-[16px] items-center py-[6px] border-t border-casevo-border-soft first:border-t-0">
          <div className="min-w-0">
            <div className="text-[13.5px] text-casevo-forest">{fmtDate(p.receivedOn)}{p.payer ? ` · ${p.payer}` : ''}</div>
            {p.note && <div className="text-[12px] text-casevo-text-soft">{p.note}</div>}
            {dateWarnings.filter((w) => w.field === `interim:${p.id}`).map((w) => <div key={w.code} className="text-[12px] text-casevo-warn mt-[2px]" role="status">{w.message}</div>)}
          </div>
          <div className="flex items-center gap-[6px]">
            <span className="text-[14px] font-semibold tabular-nums text-casevo-forest mr-[4px]">{money(p.amountPence)}</span>
            {!locked && <>
              <button type="button" className={B_GHOST_SM} disabled={!!form} onClick={() => openForm({ id: p.id, amount: p.amountPence, receivedOn: p.receivedOn, payer: p.payer, note: p.note })}>Edit</button>
              <button type="button" className={`${B_GHOST_SM} !text-casevo-danger`} disabled={!!form} onClick={() => setDel(p)}>Remove</button>
            </>}
          </div>
        </div>
      ))}
      {!interims.length && !form && <p className={`${NOTE} my-[4px]`}>None recorded.</p>}
      {form ? (
        <div className="rounded-[12px] border border-casevo-border p-[14px] mt-[10px]">
          <PenceField layout="row" label="Amount received" pence={form.amount} allowBlank onPence={(v) => setForm({ ...form, amount: v })} onBadChange={bad.track('amount')} />
          <FieldRow label="Received on"
            note={formFuture
              ? <span className={FIELD_ERROR} role="alert">{formFuture}</span>
              : formWarnings(form.receivedOn).map((w) => <span key={w.code} className="block text-[12px] text-casevo-warn mt-[4px]" role="status">{w.message}</span>)}>
            <DateInput value={form.receivedOn} onChange={(v) => setForm({ ...form, receivedOn: v })} ariaLabel="Date received" onValidityChange={(ok) => setDateTyping(!ok)} />
          </FieldRow>
          <FieldRow label="Paid by"><input aria-label="Paid by" className={`${INPUT} w-[220px]`} value={form.payer} maxLength={300} placeholder="e.g. Defendant’s insurer" onChange={(e) => setForm({ ...form, payer: e.target.value })} /></FieldRow>
          <FieldRow label="Note"><input aria-label="Note" className={`${INPUT} w-[220px]`} value={form.note} maxLength={300} onChange={(e) => setForm({ ...form, note: e.target.value })} /></FieldRow>
          <div className="flex flex-wrap items-center gap-[10px] mt-[10px]">
            <button type="button" className={B_PRIMARY} disabled={!valid || busy} onClick={() => { void save() }}>{busy ? 'Saving…' : form.id ? 'Save payment' : 'Add payment'}</button>
            <button type="button" className={B_SECONDARY} disabled={busy} onClick={() => setForm(null)}>Cancel</button>
            <UnsavedGuard dirty={dirty && !busy} />
            {bad.any && <span className="text-[12px] text-casevo-danger" role="alert">Fix the highlighted fields to save.</span>}
          </div>
        </div>
      ) : !locked && (
        <button type="button" className={`${B_SECONDARY_SM} mt-[10px]`} onClick={() => openForm({ id: null, amount: null, receivedOn: null, payer: '', note: '' })}><BIcon name="plus" size={13} />Add payment</button>
      )}
      {/* Outside the form: Remove fails with the form closed. */}
      {error && <p className="mt-2 mb-0 text-[12.5px] text-casevo-danger" role="alert">{error}</p>}
      <ConfirmDialogue isOpen={!!del} title="Remove this interim payment?" confirmLabel="Remove"
        message={del ? `${money(del.amountPence)} received ${fmtDate(del.receivedOn)} will be removed. This is recorded in the audit log.` : ''}
        onCancel={() => setDel(null)}
        onConfirm={() => { const d = del; setDel(null); if (d) deleteInterimApi(caseId, d.id).then(() => onSaved().catch(() => setError(REFRESH_FAILED)), (e) => setError(errorText(e, 'Couldn’t remove it.'))) }} />
    </FigureCard>
  )
}

// Court & proceedings — which court, the level (25% / 100% cap), the stage
// reached (picks the CFA's rate), issued on, court name, QOCS costs.
const CourtCard: React.FC<FigProps> = (p) => {
  const { m, setField } = p
  // Changing the proceedings keeps the stage reached consistent with them.
  const setIssued = (v: ProceedingsStage): void => {
    setField('proceedingsStage', v)
    if (v !== 'PRE_ISSUE' && m.stageReached === 'PRE_ISSUE') setField('stageReached', 'POST_ISSUE')
    if (v === 'PRE_ISSUE' && (m.stageReached === 'POST_ISSUE' || m.stageReached === 'LISTED_FOR_TRIAL' || m.stageReached === 'TRIAL')) setField('stageReached', 'PRE_ISSUE')
  }
  const setLevel = (v: ProceedingsLevel): void => {
    setField('proceedingsLevel', v)
    if (v === 'APPEAL') setField('stageReached', 'APPEAL')
    else if (m.stageReached === 'APPEAL') setField('stageReached', m.proceedingsStage === 'PRE_ISSUE' ? 'PRE_ISSUE' : 'POST_ISSUE')
  }
  return (
    <FigureCard icon="flag" title="Court & proceedings" sub="Where the claim is — this picks the success-fee rate and the cap" {...cardChrome(p, 'court')}>
      {p.editing ? (
        <>
          <FieldRow label="Issued" htmlFor="st-stage">
            <select id="st-stage" className={`${INPUT} w-[220px]`} value={m.proceedingsStage} onChange={(e) => setIssued(e.target.value as ProceedingsStage)}>
              {(Object.keys(STAGE_LABEL) as ProceedingsStage[]).map((k) => <option key={k} value={k}>{STAGE_LABEL[k]}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Level" htmlFor="st-level">
            <select id="st-level" className={`${INPUT} w-[220px]`} value={m.proceedingsLevel} onChange={(e) => setLevel(e.target.value as ProceedingsLevel)}>
              {(Object.keys(LEVEL_LABEL) as ProceedingsLevel[]).map((k) => <option key={k} value={k}>{LEVEL_LABEL[k]}</option>)}
            </select>
          </FieldRow>
          <FieldRow label="Stage reached" htmlFor="st-reached" hint="Picks the CFA’s success-fee rate">
            <select id="st-reached" className={`${INPUT} w-[220px]`} value={m.stageReached} onChange={(e) => setField('stageReached', e.target.value as CfaStage)}>
              {CFA_STAGES.map((k) => <option key={k} value={k}>{CFA_STAGE_LABEL[k]}</option>)}
            </select>
          </FieldRow>
        </>
      ) : (
        <>
          <ReadRow label="Issued">{STAGE_LABEL[m.proceedingsStage]}</ReadRow>
          <ReadRow label="Level">{LEVEL_LABEL[m.proceedingsLevel]}</ReadRow>
          <ReadRow label="Stage reached" hint="Picks the CFA’s success-fee rate">{CFA_STAGE_LABEL[m.stageReached]}</ReadRow>
        </>
      )}
      <DateRow p={p} k="issuedOn" label="Issued on" />
      <TextRow p={p} k="court" label="Court" placeholder="e.g. County Court at Leeds" />
      <Money p={p} k="adverseCostsOffsetPence" label="Adverse costs set off (QOCS)" hint="Costs the court ordered the client to pay (claims issued from 6 April 2023)" />
    </FigureCard>
  )
}

// Client capacity — children and protected parties need the court to approve
// a settlement and any deductions. THE DOB this case uses + any mismatch.
// "Protected party" is a saved setting → the card's edit mode.
const ClientCard: React.FC<FigProps & { onReload: () => Promise<void> }> = (p) => {
  const { s, m, setField, caseId, onReload } = p
  const [confirmAdult, setConfirmAdult] = useState(false)
  const [adultError, setAdultError] = useState<string | null>(null)
  return (
    <FigureCard icon="user" title="Client" sub="Children and protected parties need the court to approve a settlement" {...cardChrome(p, 'client')}>
      {s.client.caseDobUnreadable && (
        <div className="mb-2"><Banner tone="warn">Date of birth on this case can’t be read{s.client.dobValid ? ', so the client record’s is used' : ''}. Correct it in Client details.</Banner></div>
      )}
      {s.client.clientRecordDob && s.client.dateOfBirth && (
        <div className="mb-2"><Banner tone="warn">Date of birth on this case ({dmy(s.client.dateOfBirth)}) differs from the client record ({dmy(s.client.clientRecordDob)}). Check which is right in Client details.</Banner></div>
      )}
      {s.client.dobValid ? (
        <p className={`${NOTE} my-[4px]`}>Date of birth {dmy(s.client.dateOfBirth!)} — {s.client.childToday ? <b className="font-semibold text-casevo-forest">a child today</b> : 'an adult today'}. Age is checked on the settlement date.</p>
      ) : s.matter.adultConfirmedAt ? (
        <p className={`${NOTE} my-[4px]`}>No date of birth recorded. Confirmed an adult by {s.matter.adultConfirmedBy ?? 'a former member'} on {fmtDate(s.matter.adultConfirmedAt, 'long')}.</p>
      ) : (
        // (Audit fix batch 2, Low 14) While a statement locks the figures the
        // confirmation isn't offered — the note says why instead.
        <Banner tone="warn" title="Date of birth missing" action={p.locked ? undefined : <button type="button" className={B_SM_PRIMARY} onClick={() => setConfirmAdult(true)}>Confirm client is an adult</button>}>
          The client’s date of birth is missing, so their age can’t be checked. Add it in Client details{p.locked ? '. (Confirming they’re 18 or over waits until the statement is sent back for changes — the figures are locked.)' : ', or confirm they’re 18 or over.'}
        </Banner>
      )}
      {adultError && <p className="text-[12.5px] text-casevo-danger mt-2" role="alert">{adultError}</p>}
      {p.editing ? (
        <FieldRow label="Protected party" hint="The client lacks capacity to manage the claim (CPR 21); deductions then need court approval">
          <SettingsSwitch checked={m.protectedParty} label="Protected party" onChange={() => setField('protectedParty', !m.protectedParty)} />
        </FieldRow>
      ) : (
        <ReadRow label="Protected party" hint="The client lacks capacity to manage the claim (CPR 21); deductions then need court approval">
          {m.protectedParty ? 'Yes' : 'No'}
        </ReadRow>
      )}
      <ConfirmDialogue isOpen={confirmAdult} title="Confirm the client is an adult?" tone="primary" confirmLabel="Confirm"
        message="Record that the client is 18 or over. Your name and the time are saved with it, and it’s used until a date of birth is added."
        onCancel={() => setConfirmAdult(false)}
        onConfirm={() => {
          setConfirmAdult(false); setAdultError(null)
          confirmClientAdultApi(caseId).then(onReload).catch((e) => setAdultError(errorText(e, 'Couldn’t record that.')))
        }} />
    </FigureCard>
  )
}

export default CaseSettlementSection
